import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { companyApi } from '@/api';

import { Alert, Icon, LinkButton } from '@/components/ui';
import { AppShell } from '@/components/shell';
import { VerifyEmailGate } from '@/features/auth/VerifyEmailGate';
import { HeaderMessagesLink } from '@/features/messages/HeaderMessagesLink';
// import { UnreadBadge } from '@/features/messages/UnreadBadge';
import {
  NEEDS_VERIFICATION,
  guideAlreadyOpened,
  markGuideOpened,
} from '@/features/verification/guideSteps';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { useCompanyVerification } from '@/features/verification/useCompanyVerification';
import { useAuth } from '@/hooks/useAuth';
import { visibleProfileTabs } from '@/layouts/companyProfileTabs';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { PERMISSIONS } from '@/types/roles';

/**
 * Grouped as the company dashboard is specified: the recruitment numbers,
 * then the listings, the candidates, the matching that helps choose between
 * them, and the organisation's own team, account and plan.
 *
 * An item's `permission` hides it from members who lack it; an array means
 * any one of them is enough.
 */
const NAV = [
  {
    label: 'Recruitment dashboard',
    items: [
      { to: paths.company.dashboard, label: 'Dashboard', icon: 'dashboard' },
      {
        to: paths.company.analytics,
        label: 'Analytics',
        icon: 'chartBar',
        permission: PERMISSIONS.ANALYTICS_READ,
      },
      {
        // The Interviews page in calendar-only mode, so its actions stay in
        // one place. Its own path keeps "Interviews" lit on that page's
        // Calendar tab rather than this entry.
        to: paths.company.interviewCalendar,
        label: 'Interview calendar',
        icon: 'calendar',
        permission: PERMISSIONS.INTERVIEW_CREATE,
      },
    ],
  },
  // {
  //   label: 'Job postings',
  //   items: [
  //     {
  //       to: paths.company.internships,
  //       label: 'Internships',
  //       icon: 'briefcase',
  //       permission: PERMISSIONS.INTERNSHIP_READ,
  //     },
  //   ],
  // },
  {
    label: 'Candidates',
    items: [
      // {
      //   to: paths.company.applications,
      //   label: 'Applications',
      //   icon: 'clipboard',
      //   permission: PERMISSIONS.APPLICATION_READ,
      // },
      {
        to: paths.company.candidates,
        // label: 'Find candidates',
        label: 'Find Talent',
        icon: 'userSearch',
        permission: PERMISSIONS.CANDIDATE_READ,
        // Saved candidates is a tab of this page; stay lit on it.
        also: [paths.company.talentPool],
      },
      {
        to: paths.company.interviews,
        label: 'Interviews',
        icon: 'calendarClock',
        permission: PERMISSIONS.INTERVIEW_CREATE,
      },
      // {
      //   to: paths.company.offers,
      //   label: 'Offers',
      //   icon: 'contract',
      //   permission: PERMISSIONS.OFFER_CREATE,
      // },
      // Messages sits beside the header bell now: see HeaderMessagesLink.
      // {
      //   to: paths.company.messages,
      //   label: 'Messages',
      //   icon: 'chat',
      //   suffix: <UnreadBadge />,
      //   permission: PERMISSIONS.MESSAGE_READ,
      // },
    ],
  },
  // AI features: hidden from the sidebar for now. Uncomment to bring back.
  // {
  //   label: 'AI features',
  //   items: [
  //     {
  //       to: paths.company.recommendations,
  //       label: 'AI recommendations',
  //       icon: 'sparkles',
  //       permission: [PERMISSIONS.CANDIDATE_READ, PERMISSIONS.APPLICATION_READ],
  //     },
  //     {
  //       // Applications ordered by match score, lit only while that order is on.
  //       to: `${paths.company.applications}?sort=match`,
  //       label: 'Ranked applicants',
  //       icon: 'sort',
  //       permission: PERMISSIONS.APPLICATION_READ,
  //     },
  //   ],
  // },
  // {
  //   label: 'People',
  //   items: [
  //     { to: paths.company.team, label: 'Team', icon: 'team' },
  //     {
  //       to: paths.company.roles,
  //       label: 'Roles and permissions',
  //       icon: 'userCog',
  //       permission: PERMISSIONS.COMPANY_MEMBERS_MANAGE,
  //     },
  //   ],
  // },
  {
    label: 'Account',
    items: [
      // Profile, Employer brand and My account (where the account is closed),
      // as tabs on one page. Every member has at least My account, so the
      // entry always shows; it opens the first tab this member may use.
      { key: 'profile', label: 'Company profile', icon: 'building' },
      // {
      //   to: paths.company.verification,
      //   label: 'Verification',
      //   icon: 'shieldCheck',
      //   permission: PERMISSIONS.COMPANY_PROFILE_UPDATE,
      // },
      // Integrations: hidden from the sidebar for now. Uncomment to bring back.
      // {
      //   to: paths.company.integrations,
      //   label: 'Integrations',
      //   icon: 'plug',
      //   permission: PERMISSIONS.ATS_READ,
      // },
    ],
  },
  {
    label: 'Subscription & billing',
    items: [
      {
        to: paths.company.billing,
        label: 'Plan and billing',
        icon: 'creditCard',
        permission: PERMISSIONS.SUBSCRIPTION_READ,
      },
    ],
  },
];

/** The profile entry points at the first tab this member may open, and stays
 * lit on all three tabs. */
function withProfileEntry(item, can) {
  if (item.key !== 'profile') return item;
  const [first, ...rest] = visibleProfileTabs(can);
  const { key: _key, ...entry } = item;
  return { ...entry, to: first.to, also: rest.map((tab) => tab.to) };
}

/** An item with a `permission` shows only to members who hold it. */
function visibleNav(can) {
  return NAV.map((group) => ({
    ...group,
    items: group.items
      .filter(
        (item) =>
          !item.permission ||
          (Array.isArray(item.permission) ? item.permission.some(can) : can(item.permission)),
      )
      .map((item) => withProfileEntry(item, can))
      .map(({ permission: _permission, ...item }) => item),
  })).filter((group) => group.items.length > 0);
}

/** Signed-in company shell. */
export function CompanyLayout() {
  const { user, signOut } = useAuth();
  const { can } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  // Unverified companies are walked through verification. Only members who
  // may edit the profile can act on it, and the API needs a confirmed email.
  const canVerify = Boolean(user?.is_email_verified) && can(PERMISSIONS.COMPANY_PROFILE_UPDATE);
  const { data: verification } = useQuery({
    queryKey: ['company-verification'],
    queryFn: companyApi.getVerification,
    enabled: canVerify,
    staleTime: 60_000,
  });
  const needsVerification = canVerify && NEEDS_VERIFICATION.has(verification?.verification_status);
  const onGuide = location.pathname === paths.company.verificationGuide;
  // The verification pages show the status and the reason themselves, so the
  // banner would only repeat them there.
  const onVerification = onGuide || location.pathname === paths.company.verification;
  // The one place an unverified workspace is told why posting and inviting are
  // closed -- shown to every member, while under review too. The pages only
  // swap their buttons for "Verify to continue".
  const {
    status: companyStatus,
    needsVerification: locked,
    rejectionReason,
    logoUrl,
  } = useCompanyVerification();
  const underReview = companyStatus === 'PENDING';
  const rejected = companyStatus === 'REJECTED';

  // Right after sign-in (which lands on the overview), open the guide once per
  // session. A deep link from an email or a notification is left alone.
  useEffect(() => {
    if (!needsVerification || location.pathname !== paths.company.dashboard) return;
    if (guideAlreadyOpened(user.id)) return;
    markGuideOpened(user.id);
    navigate(paths.company.verificationGuide);
  }, [needsVerification, location.pathname, navigate, user?.id]);

  return (
    <AppShell
      nav={visibleNav(can)}
      sectionLabel="Company"
      company={{ name: user?.company?.company_name ?? 'Company', logoUrl }}
      roleLabel={user?.company?.company_name ?? 'Company'}
      user={user}
      profileTo={visibleProfileTabs(can)[0].to}
      accountMenu={[
        ...(can(PERMISSIONS.COMPANY_PROFILE_UPDATE)
          ? [{ to: paths.company.profile, label: 'Company profile', icon: 'building' }]
          : []),
        // "My account" opens on the password card.
        { to: paths.company.access, label: 'My account & password', icon: 'shieldCheck' },
        ...(can(PERMISSIONS.SUBSCRIPTION_READ)
          ? [{ to: paths.company.billing, label: 'Plan and billing', icon: 'creditCard' }]
          : []),
        { to: paths.notifications, label: 'Notifications', icon: 'bell' },
      ]}
      onSignOut={() => void signOut()}
      // headerRight={<NotificationBell />}
      headerRight={
        <>
          {can(PERMISSIONS.MESSAGE_READ) && <HeaderMessagesLink to={paths.company.messages} />}
          <NotificationBell />
        </>
      }
      banner={
        <>
          {/* Rejected: say why, in the admin's words, wherever the team is --
              not only in the email that went to the owner. */}
          {/* The guide shows the reason in its own notice, next to the form. */}
          {locked && rejected && !onVerification && (
            <Alert tone="error" className="mb-6" title="Your verification request was not approved">
              {rejectionReason && (
                <p className="break-words whitespace-pre-line">
                  <span className="font-medium">Reason:</span> {rejectionReason}
                </p>
              )}
              <p className={rejectionReason ? 'mt-1' : undefined}>
                {canVerify ? (
                  <>
                    Fix it and submit again.{' '}
                    <Link to={paths.company.verification} className="font-medium underline">
                      Go to verification
                    </Link>
                    .
                  </>
                ) : (
                  'Ask a workspace admin to fix it and submit again.'
                )}
              </p>
            </Alert>
          )}
          {/* The one thing standing between a new company and the product, so
              it is louder than an inline notice: an accent bar, a marked icon
              and a real button rather than a link in the sentence. */}
          {locked && !rejected && !onVerification && (
            <div
              role="status"
              className="border-warning-200 bg-warning-100/70 rounded-card border-l-warning-600 mb-6 flex flex-col gap-3 border border-l-4 px-4 py-3.5 shadow-sm sm:flex-row sm:items-center sm:gap-4"
            >
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <span
                  aria-hidden="true"
                  className="bg-warning-600 grid size-9 shrink-0 place-items-center rounded-full text-white"
                >
                  <Icon name={underReview ? 'clock' : 'alert'} size={18} />
                </span>
                <div className="min-w-0">
                  <p className="text-warning-800 text-[0.9375rem] font-semibold">
                    {underReview
                      ? 'We are reviewing your company details'
                      : 'Your company is not verified yet'}
                  </p>
                  <p className="text-warning-700 mt-0.5 text-sm">
                    {underReview
                      ? 'Our team is checking the documents you sent. This usually takes one or two working days, and we will notify you when it is done. Until then, you cannot post internships or invite your team.'
                      : 'You can post internships and invite your team once your company is verified.'}
                    {!canVerify && !underReview && ' Ask a workspace admin to complete it.'}
                  </p>
                </div>
              </div>
              {canVerify && (
                <LinkButton
                  to={underReview ? paths.company.verification : paths.company.verificationGuide}
                  size="sm"
                  className="shrink-0 self-start sm:self-center"
                >
                  {underReview ? 'See the status' : 'Continue verification'}
                </LinkButton>
              )}
            </div>
          )}
        </>
      }
    >
      <VerifyEmailGate>
        <Outlet />
      </VerifyEmailGate>
    </AppShell>
  );
}
